import { faGlobe, faTag } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect } from "react";
import { useGetDefInContext, SourceLocation, WordView } from "../api-generated";
import Ellipsis from "./Ellipsis";
import Modal from "./Modal";

const TileButton: React.FC<{
  children: React.ReactNode;
  colorClasses: string;
  onClick: () => void;
}> = ({ children, colorClasses, onClick }) => {
  return (
    <div
      className={`w-1/2 border rounded h-full text-center flex flex-col justify-center ${colorClasses}`}
      onClick={onClick}
    >
      <div className="text-center w-full">{children}</div>
    </div>
  );
};

interface Props {
  location: SourceLocation;
  word: WordView;
  isOpen: boolean;
  onClose: () => void;
  onTriggerSearchGoogle: () => void;
  onTriggerSayWhat: () => void;
  onTriggerGotIt: () => void;
  onTriggerDontGotIt: () => void;
  onTriggerMarkAsUnopened: () => void;
  onTriggerIgnore: () => void;
  onTriggerHmmInteresting: () => void;
  onTriggerTagWord: () => void;
  onTriggerTagSentence: () => void;
}

const MobileWordPopup: React.FC<Props> = ({
  location,
  word,
  isOpen,
  onClose,
  onTriggerSearchGoogle,
  onTriggerSayWhat,
  onTriggerGotIt,
  onTriggerDontGotIt,
  onTriggerIgnore,
  onTriggerHmmInteresting,
  onTriggerTagWord,
  onTriggerTagSentence,
}: Props) => {
  const {
    mutate,
    isLoading: isLoadingSimpleDef,
    isError: isErrorSimpleDef,
    data,
  } = useGetDefInContext();
  useEffect(() => mutate({ data: { location } }), [JSON.stringify(location)]);
  const modalStyles = {
    content: {
      width: "80%", // 80% of the screen width
      height: "80%", // 60% of the screen height
      minWidth: "300px", // Minimum width
      minHeight: "200px", // Minimum height
      //position: "fixed",
      top: "50%", // Center vertically
      left: "50%", // Center horizontally
      transform: "translate(-50%, -50%)", // Shift by 50% to perfectly center
    },
  };

  return (
    <Modal isOpen={isOpen} onTriggerClose={onClose} style={modalStyles}>
      <div className="text-xl font-bold text-center pb-2">{word.string}</div>

      <div className="text-med text-center pb-12 h-2">
        {!isLoadingSimpleDef && !isErrorSimpleDef && data ? (
          <>{data.join(", ")}</>
        ) : (
          <Ellipsis />
        )}
      </div>

      <div
        className="flex flex-col gap-2"
        //SNIPPET
      >
        <div className="flex flex-col">
          <div className="h-24 flex gap-2">
            <TileButton
              colorClasses="border-amber-400 bg-amber-100"
              onClick={onTriggerSayWhat}
            >
              <span>Say what?</span>
            </TileButton>
            <TileButton
              colorClasses="border-pink-400 bg-pink-100"
              onClick={onTriggerHmmInteresting}
            >
              <span>Hmm Interesting</span>
            </TileButton>
          </div>
        </div>

        <div className="h-24 flex flex-row gap-2">
          <TileButton
            colorClasses="border-gray-400 bg-gray-50"
            onClick={onTriggerDontGotIt}
          >
            <span>Learning</span>
          </TileButton>
          <TileButton
            colorClasses="border-gray-400 bg-gray-50"
            onClick={onTriggerGotIt}
          >
            <span>Got it</span>
          </TileButton>
        </div>

        <div className="h-24 flex flex-row gap-2">
          <TileButton
            colorClasses="border-gray-400 bg-gray-50"
            onClick={onTriggerSearchGoogle}
          >
            <span>
              <FontAwesomeIcon icon={faGlobe} /> Search Google
            </span>
          </TileButton>

          <TileButton
            colorClasses="border-gray-400 bg-gray-50"
            onClick={onTriggerIgnore}
          >
            <span>Ignore</span>
          </TileButton>
        </div>

        <div className="h-24 flex flex-row gap-2">
          <TileButton
            colorClasses="border-gray-400 bg-gray-50"
            onClick={onTriggerTagWord}
          >
            <span>
              <FontAwesomeIcon icon={faTag} /> Tag Word
            </span>
          </TileButton>

          <TileButton
            colorClasses="border-gray-400 bg-gray-50"
            onClick={onTriggerTagSentence}
          >
            <span>
              <FontAwesomeIcon icon={faTag} /> Tag Sentence/Line
            </span>
          </TileButton>
        </div>
      </div>
    </Modal>
  );
};

export default MobileWordPopup;
